/**
 * Configuración de Roles del Sistema
 */

import {
  ShieldCheckIcon,
  BriefcaseIcon,
  FolderIcon,
  UserGroupIcon,
  UserIcon,
  MegaphoneIcon,
  LifebuoyIcon,
  EyeIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  CubeIcon,
  BuildingStorefrontIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline'
import type { UserRole } from '../types'

export interface RoleConfig {
  icon: typeof ShieldCheckIcon
  name: string
  description: string
  color: string
  level: number
  capabilities: string[]
  isPlatformRole?: boolean  // true = OWLY team role
}

export const ROLE_CONFIG: Record<UserRole, RoleConfig> = {
  // Platform roles (OWLY team)
  owly_admin: {
    icon: SparklesIcon,
    name: 'OWLY Admin',
    description: 'Administrador de la plataforma OWLY',
    color: 'oklch(0.70 0.25 45)', // Gold
    level: 100,
    isPlatformRole: true,
    capabilities: [
      'Gestionar todas las empresas',
      'Configurar plataforma',
      'Ver métricas globales',
      'Soporte técnico avanzado',
      'Gestionar permisos globales',
    ],
  },
  owly_support: {
    icon: WrenchScrewdriverIcon,
    name: 'OWLY Support',
    description: 'Soporte técnico de la plataforma',
    color: 'oklch(0.65 0.20 180)', // Teal
    level: 80,
    isPlatformRole: true,
    capabilities: [
      'Ver empresas y usuarios',
      'Soporte técnico',
      'Diagnóstico de problemas',
      'Gestión de tickets',
    ],
  },
  owly_3d: {
    icon: CubeIcon,
    name: 'OWLY 3D',
    description: 'Equipo 3D - Gestión de orbit views y contenido 3D',
    color: 'oklch(0.60 0.22 280)', // Purple-blue
    level: 70,
    isPlatformRole: true,
    capabilities: [
      'Gestionar orbit views de todos los proyectos',
      'Subir y editar contenido 3D',
      'Ver proyectos y unidades',
      'Acceso cross-company a assets 3D',
    ],
  },
  owly_reviewer: {
    icon: EyeIcon,
    name: 'OWLY Reviewer',
    description: 'Revisor de plataforma - Revisión de integraciones, leads y configuraciones',
    color: 'oklch(0.65 0.18 220)', // Light blue
    level: 60,
    isPlatformRole: true,
    capabilities: [
      'Ver integraciones de todas las empresas',
      'Revisar configuraciones de leads',
      'Acceso de lectura cross-company',
      'Validar webhooks y conexiones',
    ],
  },
  owly_agency: {
    icon: BuildingStorefrontIcon,
    name: 'Agency',
    description: 'Agencia de marketing externa - Acceso a proyectos asignados',
    color: 'oklch(0.65 0.22 130)', // Green-teal
    level: 50,
    isPlatformRole: true,
    capabilities: [
      'Conectar Facebook Ads a proyectos asignados',
      'Ver analytics de marketing',
      'Acceso restringido a proyectos asignados',
      'Exportar reportes de campañas',
    ],
  },
  owly_broker: {
    icon: BuildingOffice2Icon,
    name: 'Broker',
    description: 'Corredor inmobiliario externo - Acceso a proyectos asignados',
    color: 'oklch(0.65 0.22 270)', // Purple
    level: 74,
    isPlatformRole: true,
    capabilities: [
      'Registrar prospectos en proyectos asignados',
      'Ver estado de leads referidos',
      'Seguimiento de comisiones',
      'Dashboard de broker',
    ],
  },
  // Tenant roles (Client companies)
  admin: {
    icon: ShieldCheckIcon,
    name: 'Administrador',
    description: 'Control total del sistema',
    color: 'oklch(0.65 0.25 285)', // Purple
    level: 100,
    capabilities: [
      'Gestionar todos los usuarios',
      'Configurar sistema',
      'Ver todos los proyectos',
      'Acceso a analytics avanzados',
      'Gestionar permisos',
    ],
  },
  manager: {
    icon: BriefcaseIcon,
    name: 'Gerente',
    description: 'Gestión de la empresa',
    color: 'oklch(0.55 0.20 250)', // Blue
    level: 90,
    capabilities: [
      'Gestionar usuarios de la empresa',
      'Ver todos los proyectos de la empresa',
      'Acceso a analytics',
      'Aprobar cotizaciones',
      'Gestionar equipos',
    ],
  },
  project_manager: {
    icon: FolderIcon,
    name: 'Gerente de Proyecto',
    description: 'Gestión de proyectos específicos',
    color: 'oklch(0.60 0.18 150)', // Green
    level: 70,
    capabilities: [
      'Gestionar proyectos asignados',
      'Asignar leads a asesores',
      'Ver performance de equipo',
      'Aprobar cotizaciones de proyectos',
    ],
  },
  sales_leader: {
    icon: UserGroupIcon,
    name: 'Líder de Sala',
    description: 'Liderazgo de equipo de ventas',
    color: 'oklch(0.65 0.22 50)', // Orange
    level: 60,
    capabilities: [
      'Gestionar equipo de asesores',
      'Asignar y reasignar leads',
      'Ver performance de equipo',
      'Coaching y seguimiento',
    ],
  },
  sales_advisor: {
    icon: UserIcon,
    name: 'Asesor de Ventas',
    description: 'Ventas y atención a clientes',
    color: 'oklch(0.60 0.15 200)', // Cyan
    level: 40,
    capabilities: [
      'Gestionar leads asignados',
      'Crear cotizaciones',
      'Registrar actividades',
      'Actualizar pipeline',
    ],
  },
  marketing: {
    icon: MegaphoneIcon,
    name: 'Marketing',
    description: 'Gestión de marketing y campañas',
    color: 'oklch(0.70 0.20 350)', // Pink
    level: 50,
    capabilities: [
      'Crear campañas',
      'Ver analytics de marketing',
      'Gestionar contenido',
      'Generar reportes',
    ],
  },
  support: {
    icon: LifebuoyIcon,
    name: 'Soporte',
    description: 'Atención y soporte a clientes',
    color: 'oklch(0.75 0.18 90)', // Yellow
    level: 30,
    capabilities: [
      'Ver tickets',
      'Responder consultas',
      'Acceso a knowledge base',
      'Escalar problemas',
    ],
  },
  reviewer: {
    icon: EyeIcon,
    name: 'Revisor',
    description: 'Acceso limitado para revisión externa',
    color: 'oklch(0.65 0.15 180)', // Teal
    level: 10,
    capabilities: [
      'Ver integraciones',
      'Conectar integraciones de prueba',
      'Ver leads',
    ],
  },
}

// All roles (platform + tenant)
export const ALL_ROLES: UserRole[] = [
  'owly_admin',
  'owly_support',
  'owly_3d',
  'owly_reviewer',
  'owly_agency',
  'owly_broker',
  'admin',
  'manager',
  'project_manager',
  'sales_leader',
  'sales_advisor',
  'marketing',
  'support',
  'reviewer',
]

// Platform roles (OWLY team only)
export const PLATFORM_ROLES: UserRole[] = [
  'owly_admin',
  'owly_support',
  'owly_3d',
  'owly_reviewer',
  'owly_agency',
  'owly_broker',
]

// Tenant roles (client companies)
export const USER_ROLES: UserRole[] = [
  'admin',
  'manager',
  'project_manager',
  'sales_leader',
  'sales_advisor',
  'marketing',
  'support',
  'reviewer',
]

export const STATUS_CONFIG = {
  active: {
    label: 'Activo',
    color: 'oklch(0.60 0.18 150)', // Green
    icon: 'CheckCircleIcon',
  },
  inactive: {
    label: 'Inactivo',
    color: 'oklch(0.50 0.05 270)', // Gray
    icon: 'XCircleIcon',
  },
  suspended: {
    label: 'Suspendido',
    color: 'oklch(0.60 0.22 30)', // Red-orange
    icon: 'ExclamationCircleIcon',
  },
}
